import graphql from "babel-plugin-relay/macro";
import { groupBy, partition } from "lodash";
import { useFragment } from "react-relay";

import { getFacilityMemberPermissions } from "@/hooks/getFacilityMemberPermissions";
import { useFlags } from "@/utils/launchDarkly";

import { DepartmentCard_department$key } from "../components/__generated__/DepartmentCard_department.graphql";
import { FacilityCard_Facility$key } from "../components/__generated__/FacilityCard_Facility.graphql";
import { useGetBookTodayData_club$key } from "./__generated__/useGetBookTodayData_club.graphql";

export type BookTodayData = FacilityBookTodayData | DepartmentBookTodayData;

type FacilityBookTodayData = {
  type: "facility";
  id?: string;
  ref: FacilityCard_Facility$key | null;
};

type DepartmentBookTodayData = {
  type: "department";
  id?: string;
  ref: DepartmentCard_department$key | null;
};

export const useGetBookTodayData = (
  clubRef: useGetBookTodayData_club$key | null
): BookTodayData[] => {
  const { patronDepartmentAvailability } = useFlags();
  const club = useFragment(useGetBookTodayDataFragmentString, clubRef);
  const facilities =
    club?.facilities?.edges?.flatMap((f) => (f?.node ? [f.node] : [])) || [];

  const accessibleFacilities = facilities.filter(
    (facility) =>
      "FACILITY_PERMISSIONS_DISABLED" !==
      getFacilityMemberPermissions({ fragmentRef: facility }).memberAccess
  );

  const [diningFacilities, nonDiningFacilities] = partition(
    accessibleFacilities,
    (f) => f?.type === "DINING"
  );

  const diningMap = groupBy(diningFacilities, (f) => f?.department?.id);

  const diningDepartments: BookTodayData[] = Object.values(diningMap).map(
    (dining) => {
      return {
        type: "facility" as const,
        id: dining[0]?.id,
        ref: dining[0],
      };
    }
  );

  if (!patronDepartmentAvailability) {
    // If the feature flag is off, we return all accessible facilities
    // as facility cards and dining as one department
    return [
      ...nonDiningFacilities.map((f) => ({
        type: "facility" as const,
        id: f?.id,
        ref: f,
      })),
      ...diningDepartments,
    ];
  }

  const [golfCourseFacilities, otherFacilities] = partition(
    nonDiningFacilities,
    (f) => f?.type === "GOLF_COURSE"
  );

  const golfCourseMap = groupBy(golfCourseFacilities, (f) => f?.department?.id);

  const golfCourseDepartments: BookTodayData[] = Object.values(
    golfCourseMap
  ).map((golfCourses) => {
    if (golfCourses.length > 1) {
      return {
        type: "department" as const,
        id: golfCourses[0]?.department?.id ?? undefined,
        ref: golfCourses[0]?.department ?? null,
      };
    } else {
      return {
        type: "facility" as const,
        id: golfCourses[0]?.id,
        ref: golfCourses[0],
      };
    }
  });

  return [
    ...golfCourseDepartments,
    ...otherFacilities.map((f) => ({
      type: "facility" as const,
      id: f?.id,
      ref: f,
    })),
    ...diningDepartments,
  ];
};

const useGetBookTodayDataFragmentString = graphql`
  fragment useGetBookTodayData_club on Club {
    facilities {
      edges {
        node {
          id
          type
          ...getFacilityMemberPermissions_facility
          ...FacilityCard_Facility
          department {
            id
            ...DepartmentCard_department
          }
        }
      }
    }
  }
`;
